import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    n: "01",
    title: "When",
    accent: "the proper time",
    body: "Somewhere in the world, at a particular moment in the year, a simple ingredient becomes perfect: asparagus pulled from spring soil, truffles unearthed after autumn rain, cherries bursting under early summer sun. These moments are brief, but they define how and where we should eat.",
  },
  {
    n: "02",
    title: "Where",
    accent: "the perfect place",
    body: "Some foods simply do not travel well — not across oceans, and not across seasons. They belong to a landscape, to a harvest window, to a cultural rhythm that has been repeated for generations.",
  },
  {
    n: "03",
    title: "What",
    accent: "the right dish",
    body: "Food is never just food. Every dish is shaped by the land that produces it, the climate that ripens it, and the traditions that preserve it. When we eat them there, and then, they reveal their true character.",
  },
];

const rise = {
  hidden: { opacity: 0, y: 48 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.8, ease: EASE } }),
};

export default function Concept() {
  return (
    <section id="concept" data-testid="concept-section" className="relative overflow-hidden bg-paper px-6 py-24 md:px-12 md:py-36">
      <div className="mx-auto max-w-[1280px]">
        <motion.p
          variants={rise}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={0}
          className="text-[11px] font-bold uppercase tracking-[0.4em] text-punch"
        >
          The concept
        </motion.p>
        <motion.h2
          variants={rise}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={1}
          className="mt-5 font-display text-[clamp(2.6rem,6vw,5.5rem)] font-extrabold uppercase leading-[0.9] tracking-tight text-navy"
        >
          One year.
          <br />
          <span className="text-punch">Twelve cravings.</span>
        </motion.h2>
        <motion.p
          variants={rise}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={2}
          className="mt-8 max-w-2xl text-base leading-relaxed text-navy/85 md:text-lg"
        >
          Our new 2027 calendar is far more than a simple guide to what to eat, and where and when to
          eat it: it is a time capsule of these culinary rituals as they exist today. Treasure it. Follow
          its evolution. In ten years’ time, this calendar could look completely different.
        </motion.p>

        <div className="mt-16 grid gap-10 md:mt-20 md:grid-cols-3 md:gap-8">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.n}
              variants={rise}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i + 1}
              className="group border-t-4 border-navy pt-6"
              data-testid={`concept-chapter-${c.n}`}
            >
              <div className="font-display text-6xl font-extrabold leading-none text-transparent transition-all duration-300 [-webkit-text-stroke:2px_#2E3192] group-hover:[-webkit-text-stroke:2px_#F2629E] md:text-7xl">
                {c.n}
              </div>
              <h3 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-tight text-navy">{c.title}</h3>
              <p className="mt-1 font-serif text-xl italic text-punch">{c.accent}</p>
              <p className="mt-4 text-sm leading-relaxed text-navy/80 md:text-base">{c.body}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
